import { type Dispatch, type SetStateAction,useCallback, useMemo, useRef, useState } from "react";

import type { AssignmentFormState } from "../forms/AssignmentForm";
import type { ClearReactionFormState } from "../forms/ClearReactionForm";
import type { DecisionFormState } from "../forms/DecisionForm";
import type { FilterFormState } from "../forms/FilterForm";
import type { LoopFormState } from "../forms/LoopForm";
import type { QueryBuilderFormState } from "../forms/QueryBuilderForm";
import { isFreeResponseReactionType, isUserConfirmationPopupReactionType } from "../constants";
import type { ActionFormState } from "../forms/ActionForm";
import type { EventListenerFormState } from "../forms/EventListenerForm";
import type { ReactionFormState } from "../forms/ReactionForm";
import type { SortFormState } from "../forms/SortForm";
import type { StopFormState } from "../forms/StopForm";
import { deleteNodePreservingDecisionEdges } from "../nodes/nodeFactory";
import type { RuleEdge } from "../types/edges";
import type { ActionRuleNode, AssignmentRuleNode, ClearReactionRuleNode, DecisionRuleNode, EventListenerRuleNode, FilterRuleNode, LoopRuleNode, QueryRuleNode, ReactionRuleNode, RuleNode, SortRuleNode, StopRuleNode } from "../types/nodes";
import { inferValueCategoryFromEntityFields, isDecisionValueEntityRow } from "../utils/decision-statement-value-utils";
import { buildScreenFieldRef } from "../utils/data-field-type-utils";
import { normalizeStopTagNames } from "../utils/stop-tag-utils";

type FormModalKey =
	| "query_builder"
	| "assignment"
	| "reaction"
	| "clearReaction"
	| "decision"
	| "filter"
	| "sort"
	| "loop"
	| "eventListener"
	| "action"
	| "stop"
	| "reactionTextArea";

type FormModalState = Record<FormModalKey, boolean>;

const DEFAULT_FORM_STATE: FormModalState = {
	query_builder: false,
	assignment: false,
	reaction: false,
	clearReaction: false,
	decision: false,
	filter: false,
	sort: false,
	loop: false,
	eventListener: false,
	action: false,
	stop: false,
	reactionTextArea: false,
};

type FormNodeTracking = {
	activeNodeId: string | null;
	pendingNewNodeId: string | null;
	pendingRelatedNodeIds: string[];
	/**
	 * QA: when a new node is inserted between two existing nodes, this holds
	 * the original edge that was replaced (e.g. A→B). On cancel-without-save
	 * we recreate it so the user doesn't get orphaned nodes.
	 */
	pendingRestoreEdge: RuleEdge | null;
};

const EMPTY_TRACKING: FormNodeTracking = {
	activeNodeId: null,
	pendingNewNodeId: null,
	pendingRelatedNodeIds: [],
	pendingRestoreEdge: null,
};

const buildInitialTracking = (): Record<FormModalKey, FormNodeTracking> => {
	const tracking = {} as Record<FormModalKey, FormNodeTracking>;
	for (const key of Object.keys(DEFAULT_FORM_STATE) as FormModalKey[]) {
		tracking[key] = { ...EMPTY_TRACKING };
	}
	return tracking;
};

type OpenFormOptions = {
	isNew?: boolean;
	relatedNodeIds?: string[];
	/**
	 * QA: the original edge that was removed when this new node was
	 * inserted. Re-added on cancel-without-save so the upstream node
	 * reconnects to its prior downstream target.
	 */
	restoreEdge?: RuleEdge | null;
};

type UseFormModalsOptions = {
	initialState?: Partial<FormModalState>;
	nodes: RuleNode[];
	edges: RuleEdge[];
	setNodes: Dispatch<SetStateAction<RuleNode[]>>;
	setEdges: Dispatch<SetStateAction<RuleEdge[]>>;
};

/**
 * Used when cancelling a new node before save.
 * - **Decision** node(s) in `nodeIds`: delete each via `deleteNodePreservingDecisionEdges` first so any
 *   incoming decision-true / decision-false edge from an **upstream** decision stays (retargeted to a
 *   ghost). Then remove any remaining ids (e.g. branch ghosts of the unfinished decision) with a
 *   simple node/edge strip.
 * - **No** decision in `nodeIds`: remove each id via `deleteNodePreservingDecisionEdges` only (same
 *   preservation for decision branches from existing decisions).
 */
const removeNodesAndEdges = (
	nodeIds: string[],
	nodes: RuleNode[],
	edges: RuleEdge[],
	setNodes: Dispatch<SetStateAction<RuleNode[]>>,
	setEdges: Dispatch<SetStateAction<RuleEdge[]>>,
) => {
	const idSet = new Set(nodeIds);
	const decisionIdsToRemove = nodeIds.filter((id) => {
		const n = nodes.find((x) => x.id === id);
		return n?.type === "decision" && idSet.has(id);
	});
	const removesDecisionNode = decisionIdsToRemove.length > 0;

	if (removesDecisionNode) {
		let nextNodes = nodes;
		let nextEdges = edges;
		for (const id of decisionIdsToRemove) {
			if (!nextNodes.some((n) => n.id === id)) continue;
			const result = deleteNodePreservingDecisionEdges(id, nextNodes, nextEdges);
			nextNodes = result.nodes;
			nextEdges = result.edges;
		}
		const remainingIds = nodeIds.filter((id) => !decisionIdsToRemove.includes(id));
		if (remainingIds.length > 0) {
			const remSet = new Set(remainingIds);
			nextNodes = nextNodes.filter((n) => !remSet.has(n.id));
			nextEdges = nextEdges.filter((e) => !remSet.has(e.source) && !remSet.has(e.target));
		}
		setNodes(nextNodes);
		setEdges(nextEdges);
		return;
	}

	let nextNodes = nodes;
	let nextEdges = edges;
	for (const id of nodeIds) {
		if (!nextNodes.some((n) => n.id === id)) continue;
		const result = deleteNodePreservingDecisionEdges(id, nextNodes, nextEdges);
		nextNodes = result.nodes;
		nextEdges = result.edges;
	}
	setNodes(nextNodes);
	setEdges(nextEdges);
};

const useFormModals = ({ initialState = {}, nodes, edges, setNodes, setEdges }: UseFormModalsOptions) => {
	const [formState, setFormState] = useState<FormModalState>({ ...DEFAULT_FORM_STATE, ...initialState });
	const [tracking, setTracking] = useState(buildInitialTracking);

	const trackingRef = useRef(tracking);
	trackingRef.current = tracking;


	const openNodeForm = useCallback((key: FormModalKey, nodeId: string | null, options?: OpenFormOptions) => {
		if (!nodeId) return;
		setTracking((prev) => {
			// Single-form invariant: opening one form clears tracking on all
			// others so a previously-open form's "pending new node" doesn't
			// linger and trigger a stale-cleanup on close.
			const cleared = buildInitialTracking();
			cleared[key] = {
				activeNodeId: nodeId,
				pendingNewNodeId: options?.isNew ? nodeId : null,
				pendingRelatedNodeIds: options?.isNew && options?.relatedNodeIds?.length ? options.relatedNodeIds : [],
				pendingRestoreEdge: options?.isNew && options?.restoreEdge ? options.restoreEdge : null,
			};
			return cleared;
		});
		// Close any currently-open form, then open the requested one. Only
		// one form is allowed open at a time across the rule builder.
		setFormState({ ...DEFAULT_FORM_STATE, [key]: true });
	}, []);

	const closeNodeForm = useCallback((key: FormModalKey, options?: { fromSave?: boolean }) => {
		const t = trackingRef.current[key];
		if (options?.fromSave !== true && t.activeNodeId && t.activeNodeId === t.pendingNewNodeId) {
			removeNodesAndEdges([t.activeNodeId, ...t.pendingRelatedNodeIds], nodes, edges, setNodes, setEdges);
			if (t.pendingRestoreEdge) {
				const restore = t.pendingRestoreEdge;
				setEdges((current) => {
					if (current.some((e) => e.id === restore.id)) return current;
					return [...current, restore];
				});
			}
		}
		setTracking((prev) => ({ ...prev, [key]: { ...EMPTY_TRACKING } }));
		setFormState((prev) => ({ ...prev, [key]: false }));
	}, [nodes, edges, setNodes, setEdges]);

	/**
	 * Close (and clear) whichever form is currently bound to `nodeId`. Used when a
	 * node is deleted from the canvas so its left-panel form doesn't linger. Uses
	 * the save-path semantics (no cancel cleanup) since the node is being removed
	 * explicitly — we just drop the form's tracking + visibility. No-op if no open
	 * form belongs to the node.
	 */
	const closeFormForNode = useCallback((nodeId: string) => {
		const current = trackingRef.current;
		for (const key of Object.keys(current) as FormModalKey[]) {
			if (current[key].activeNodeId !== nodeId) continue;
			setTracking((prev) => ({ ...prev, [key]: { ...EMPTY_TRACKING } }));
			setFormState((prev) => ({ ...prev, [key]: false }));
		}
	}, []);

	/**
	 * Updates the matching node's data via `mapToNodeData`, then closes the form.
	 * Each form type provides its own mapping function to translate form state → node data.
	 */
	const updateNodeAndClose = useCallback(<TFormState,>(
		key: FormModalKey,
		nodeType: string,
		state: TFormState,
		mapToNodeData: (state: TFormState) => Record<string, unknown>,
	) => {
		const activeNodeId = trackingRef.current[key].activeNodeId;
		if (!activeNodeId) return;
		setNodes((currentNodes) => currentNodes.map((node) => {
			if (node.id !== activeNodeId || node.type !== nodeType) return node;
			return { ...node, data: { ...node.data, ...mapToNodeData(state) } } as typeof node;
		}));
		if (key !== "stop") {
			closeNodeForm(key, { fromSave: true });
		}
	}, [closeNodeForm, setNodes]);

	const resetForms = useCallback(() => {
		setFormState(DEFAULT_FORM_STATE);
		setTracking(buildInitialTracking);
	}, []);

	// --- Per-form initial state derivation ---
	// Each remains a separate useMemo because the node-data → form-state mapping is unique.

	const decisionFormInitialState = useMemo<Partial<DecisionFormState> | undefined>(() => {
		const id = tracking.decision.activeNodeId;
		const node = id ? nodes.find((n): n is DecisionRuleNode => n.id === id && n.type === "decision") : undefined;
		if (!node) return undefined;
		const sorted = [...(node.data.statements ?? [])].sort((a, b) => (a.num ?? 0) - (b.num ?? 0));
		return {
			decisionName: node.data.name ?? "",
			decisionLogicExpression: (node.data.decisionLogicExpression as string | undefined) ?? "",
			matchType: node.data.matchType === "any" ? "any" : "all",
			statements: sorted.map((s, index) => {
				const entity = isDecisionValueEntityRow({
					valueDatabaseTableFieldId: s.valueDatabaseTableFieldId,
					valueScreenFieldId: s.valueScreenFieldId,
					valueAdvancedTriggerId: s.valueAdvancedTriggerId,
					valueResourceCollectionId: s.valueResourceCollectionId,
					valueSimpleVariableId: s.valueSimpleVariableId,
					valueFormulaVariableId: s.valueFormulaVariableId,
					valueDatabaseSingleRowVariableId: s.valueDatabaseSingleRowVariableId,
					valueGlobalConstant: s.valueGlobalConstant,
					valueSimpleVariableName: s.valueSimpleVariableName,
					valueFormulaVariableName: s.valueFormulaVariableName,
					valueDatabaseSingleRowVariableName: s.valueDatabaseSingleRowVariableName,
				});
				const valueBinding = entity
					? (s.valueGlobalConstant ??
						s.valueDatabaseTableFieldId ??
						s.valueScreenFieldId ??
						s.valueAdvancedTriggerId ??
						s.valueResourceCollectionId ??
						s.valueSimpleVariableName ??
						s.valueFormulaVariableName ??
						s.valueDatabaseSingleRowVariableName ??
						s.value ??
						"")
					: (s.plainTextValue ?? s.value ?? "");
				return {
					id: s.id ?? `${node.id}-statement-${index}`,
					num: s.num ?? index + 1,
					// Prefer explicit ids and mapped `resource` (formula / DB-row ids) before display names.
					resource:
						s.resourceReactionNodeId ??
						s.databaseTableFieldId ??
						s.screenFieldId ??
						s.advancedTriggerId ??
						s.resourceCollectionId ??
						s.resourceFormulaVariableId ??
						s.resource ??
						s.simpleVariableName ??
						"",
					databaseTableFieldId: s.databaseTableFieldId,
					screenFieldId: s.screenFieldId,
					advancedTriggerId: s.advancedTriggerId,
					resourceCollectionId: s.resourceCollectionId,
					collectionName: s.resourceCollection?.name ?? s.collectionName,
					databaseTableFieldName: s.databaseTableField?.name,
					screenFieldName: s.screenTableField?.name,
					screenTableField: s.screenTableField,
					advancedTriggerName: s.advancedTrigger?.name,
					simpleVariableName: s.simpleVariableName,
					formulaVariableName: s.formulaVariableName,
					resourceFormulaVariableId: s.resourceFormulaVariableId,
					resourceFormulaVariable: s.resourceFormulaVariable,
					resourceReactionNodeId: s.resourceReactionNodeId,
					resourceReactionName: s.resourceReactionName,
					operator: s.operator ?? "",
					value: valueBinding,
					plainTextValue: entity ? undefined : (s.plainTextValue ?? s.value ?? ""),
					valueDatabaseTableFieldId: s.valueDatabaseTableFieldId,
					valueScreenFieldId: s.valueScreenFieldId,
					valueAdvancedTriggerId: s.valueAdvancedTriggerId,
					valueDatabaseTableFieldName: s.valueDatabaseTableField?.name,
					valueScreenFieldName: s.valueScreenTableField?.name ?? s.valueScreenField?.name,
					valueScreenField: s.valueScreenField,
					valueScreenTableField: s.valueScreenTableField ?? s.valueScreenField,
					valueAdvancedTriggerName: s.valueAdvancedTrigger?.name,
					valueSimpleVariableName: s.valueSimpleVariableName,
					valueSimpleVariableId: s.valueSimpleVariableId,
					valueFormulaVariableId: s.valueFormulaVariableId,
					valueFormulaVariableName: s.valueFormulaVariableName,
					valueFormulaVariable: s.valueFormulaVariable,
					valueDatabaseSingleRowVariableId: s.valueDatabaseSingleRowVariableId,
					valueDatabaseSingleRowVariableName: s.valueDatabaseSingleRowVariableName,
					valueGlobalConstant: s.valueGlobalConstant,
					valueGlobalConstantName: s.valueGlobalConstantName,
					valueResourceCollectionId: s.valueResourceCollectionId,
					valueCollectionName: s.valueResourceCollection?.name ?? s.valueCollectionName,
				};
			}),
		};
	}, [nodes, tracking.decision.activeNodeId]);

	const loopFormInitialState = useMemo<Partial<LoopFormState> | undefined>(() => {
		const id = tracking.loop.activeNodeId;
		const node = id ? nodes.find((n): n is LoopRuleNode => n.id === id && n.type === "loop") : undefined;
		if (!node) return undefined;
		return {
			loopName: node.data.name ?? "",
			loopType: node.data.loopType === "table" ? "table" : "variable",
			databaseTableId: node.data.databaseTableId ?? "",
			screenTableId: node.data.screenTableId ?? "",
			databaseTableVariableId: node.data.databaseTableVariableId ?? "",
			databaseTableVariableName: node.data.databaseTableVariableName ?? "",
			tableName: node.data.tableName ?? "",
			direction: (node.data.direction === "lastToFirst" ? "lastToFirst" : "firstToFirst") as LoopFormState["direction"],
			collectionId: node.data.collectionId ?? "",
			collectionName: node.data.collectionName ?? "",
		};
	}, [nodes, tracking.loop.activeNodeId]);

	const sortFormInitialState = useMemo<Partial<SortFormState> | undefined>(() => {
		const id = tracking.sort.activeNodeId;
		const node = id ? nodes.find((n): n is SortRuleNode => n.id === id && n.type === "sort") : undefined;
		if (!node) return undefined;
		return {
			sortName: node.data.name ?? "",
			databaseTableId: node.data.databaseTableId,
			screenTableId: node.data.screenTableId,
			databaseTableVariableId: node.data.databaseTableVariableId,
			tableName: node.data.tableName ?? "",
			conditions: (node.data.conditions ?? []).map((c) => ({
				sortByDatabaseTableFieldId: c.sortByDatabaseTableFieldId,
				sortByScreenTableFieldId: c.sortByScreenTableFieldId,
				sortByFieldName: c.sortByFieldName,
				sortOrder: c.sortOrder ?? "asc",
			})),
		};
	}, [nodes, tracking.sort.activeNodeId]);

	const filterFormInitialState = useMemo<Partial<FilterFormState> | undefined>(() => {
		const id = tracking.filter.activeNodeId;
		const node = id ? nodes.find((n): n is FilterRuleNode => n.id === id && n.type === "filter") : undefined;
		if (!node) return undefined;
		return {
			filterName: node.data.name ?? "",
			databaseTableId: node.data.databaseTableId,
			screenTableId: node.data.screenTableId,
			databaseTableVariableId: node.data.databaseTableVariableId,
			tableName: node.data.tableName ?? "",
			sortByDatabaseTableFieldId: node.data.sortByDatabaseTableFieldId,
			sortByScreenTableFieldId: node.data.sortByScreenTableFieldId,
			sortOrder: node.data.sortOrder ?? "",
			recordScope: (node.data.recordScope === "allRecords" ? "allRecords" : "firstRecord") as FilterFormState["recordScope"],
			filterLogicExpression: node.data.filterLogicExpression ?? "",
			conditions: (node.data.conditions ?? []).map((condition, index) => ({
				id: `${node.id}-condition-${index}`,
				field: condition.databaseTableFieldId ?? condition.screenTableFieldId ?? condition.field ?? "",
				databaseTableFieldId: condition.databaseTableFieldId,
				screenTableFieldId: condition.screenTableFieldId,
				operator: condition.operator,
				value: String(condition.value ?? ""),
				plainTextValue: condition.plainTextValue,
				valueDatabaseTableFieldId: condition.valueDatabaseTableFieldId,
				valueScreenFieldId: condition.valueScreenFieldId,
				valueDatabaseTableFieldName: condition.valueDatabaseTableFieldName,
				valueScreenFieldName: condition.valueScreenFieldName,
				valueScreenField: condition.valueScreenField,
				valueScreenTableField: condition.valueScreenTableField,
				valueSimpleVariableName: condition.valueSimpleVariableName,
				valueSimpleVariableId: condition.valueSimpleVariableId,
				valueFormulaVariableId: condition.valueFormulaVariableId,
				valueFormulaVariableName: condition.valueFormulaVariableName,
				valueFormulaVariable: condition.valueFormulaVariable,
				valueDatabaseSingleRowVariableId: condition.valueDatabaseSingleRowVariableId,
				valueDatabaseSingleRowVariableName: condition.valueDatabaseSingleRowVariableName,
				valueGlobalConstant: condition.valueGlobalConstant,
			})),
		};
	}, [nodes, tracking.filter.activeNodeId]);

	const queryFormInitialState = useMemo<Partial<QueryBuilderFormState> | undefined>(() => {
		const id = tracking.query_builder.activeNodeId;
		const node = id ? nodes.find((n): n is QueryRuleNode => n.id === id && n.type === "query_builder") : undefined;
		if (!node) return undefined;
		const rawRecordChoice = node.data.recordChoice as string | undefined;
		const recordChoice: QueryBuilderFormState["recordChoice"] =
			rawRecordChoice === "all" || rawRecordChoice === "allRecords" ? "allRecords" : "firstRecord";
		return {
			queryName: node.data.name ?? "",
			databaseTableId: node.data.databaseTableId ?? "",
			databaseTableName: node.data.databaseTableName ?? "",
			sortByFieldId: node.data.sortByFieldId ??	 "",
			sortOrder: node.data.sortDirection === "asc" ? "Ascending" : "Descending",
			recordChoice,
			conditions: node.data.conditions?.map((condition, index) => ({
				id: `${node.id}-condition-${index}`,
				databaseTableFieldId: condition.databaseTableFieldId,
				databaseTableFieldName: condition.databaseTableFieldName ?? "",
				operator: condition.operator,
				value: String(condition.value ?? ""),
			})),
		};
	}, [nodes, tracking.query_builder.activeNodeId]);

	const reactionFormInitialState = useMemo<Partial<ReactionFormState> | undefined>(() => {
		const id = tracking.reaction.activeNodeId;
		const node = id ? nodes.find((n): n is ReactionRuleNode => n.id === id && n.type === "reaction") : undefined;
		if (!node) return undefined;
		return {
			reactionName: node.data.name ?? "",
			reactionTypeId: node.data.reactionTypeId ?? "",
			reactionTypeName: node.data.reactionTypeName ?? "",
			reactionTypeFriendlyName: node.data.reactionTypeFriendlyName ?? "",
			resource: undefined,
			screenFieldId: node.data.screenFieldId ?? "",
			screenFieldName: node.data.screenFieldName ?? "",
			reactionText: node.data.reactionText ?? "",
			minimumCharacterCount:
				node.data.minimumCharacterCount != null
					? String(node.data.minimumCharacterCount)
					: "",
			userConfirmationText: node.data.userConfirmationText ?? "",
			showConfirmationText: node.data.showConfirmationText !== false,
			caseSensitive: node.data.caseSensitive === true,
		};
	}, [nodes, tracking.reaction.activeNodeId]);

	const assignmentFormInitialState = useMemo<Partial<AssignmentFormState> | undefined>(() => {
		const id = tracking.assignment.activeNodeId;
		const node = id ? nodes.find((n): n is AssignmentRuleNode => n.id === id && n.type === "assignment") : undefined;
		if (!node) return undefined;
		const statements = (node.data.statements ?? []).map((s, index) => {
			const entity = isDecisionValueEntityRow({
				valueDatabaseTableFieldId: s.valueDatabaseTableFieldId,
				valueScreenFieldId: s.valueScreenFieldId,
				valueSimpleVariableId: s.valueSimpleVariableId,
				valueFormulaVariableId: s.valueFormulaVariableId,
				valueDatabaseSingleRowVariableId: s.valueDatabaseSingleRowVariableId,
				valueGlobalConstant: s.valueGlobalConstant,
				valueSimpleVariableName: s.valueSimpleVariableName,
				valueFormulaVariableName: s.valueFormulaVariableName,
				valueDatabaseSingleRowVariableName: s.valueDatabaseSingleRowVariableName,
			});
			const valueBinding = entity
				? (s.valueGlobalConstant ??
					s.valueDatabaseTableFieldId ??
					s.valueScreenFieldId ??
					s.valueSimpleVariableName ??
					s.valueFormulaVariableName ??
					s.valueDatabaseSingleRowVariableName ??
					s.value ??
					"")
				: (s.plainTextValue ?? s.value ?? "");
			const literalPlain =
				typeof s.plainTextValue === "string"
					? s.plainTextValue
					: typeof s.value === "string"
						? s.value
						: "";
			return {
				id: s.id ?? `${node.id}-statement-${index}`,
				variable: s.variable ?? "",
				operator: s.operator ?? "",
				value: String(valueBinding ?? ""),
				plainTextValue: entity ? undefined : literalPlain || undefined,
				valueDatabaseTableFieldId: s.valueDatabaseTableFieldId,
				valueScreenFieldId: s.valueScreenFieldId,
				valueDatabaseTableFieldName: s.valueDatabaseTableField?.name,
				valueScreenFieldName: s.valueScreenTableField?.name ?? s.valueScreenField?.name,
				valueScreenField: s.valueScreenField,
				valueScreenTableField: s.valueScreenTableField ?? s.valueScreenField,
				valueSimpleVariableName: s.valueSimpleVariableName,
				valueSimpleVariableId: s.valueSimpleVariableId,
				valueFormulaVariableId: s.valueFormulaVariableId,
				valueFormulaVariableName: s.valueFormulaVariableName,
				valueFormulaVariable: s.valueFormulaVariable,
				valueDatabaseSingleRowVariableId: s.valueDatabaseSingleRowVariableId,
				valueDatabaseSingleRowVariableName: s.valueDatabaseSingleRowVariableName,
				valueGlobalConstant: s.valueGlobalConstant,
				valueGlobalConstantName: s.valueGlobalConstantName,
				valueCategory: inferValueCategoryFromEntityFields({
					valueGlobalConstant: s.valueGlobalConstant,
					valueDatabaseTableFieldId: s.valueDatabaseTableFieldId,
					valueScreenFieldId: s.valueScreenFieldId,
					valueSimpleVariableId: s.valueSimpleVariableId,
					valueFormulaVariableId: s.valueFormulaVariableId,
					valueDatabaseSingleRowVariableId: s.valueDatabaseSingleRowVariableId,
					valueSimpleVariableName: s.valueSimpleVariableName,
					valueFormulaVariableName: s.valueFormulaVariableName,
					valueDatabaseSingleRowVariableName: s.valueDatabaseSingleRowVariableName,
				}),
			};
		});
		return {
			assignmentName: node.data.name ?? "",
			statements,
		};
	}, [nodes, tracking.assignment.activeNodeId]);

	const clearReactionFormInitialState = useMemo<Partial<ClearReactionFormState> | undefined>(() => {
		const id = tracking.clearReaction.activeNodeId;
		const node = id ? nodes.find((n): n is ClearReactionRuleNode => n.id === id && n.type === "clear_reaction") : undefined;
		if (!node) return undefined;
		return {
			clearReactionName: node.data.name ?? "",
			reactionNodeId: node.data.reactionNodeId ?? "",
			reactionNodeName: node.data.reactionNodeName ?? "",
		};
	}, [nodes, tracking.clearReaction.activeNodeId]);

	const eventListenerFormInitialState = useMemo<Partial<EventListenerFormState> | undefined>(() => {
		const id = tracking.eventListener.activeNodeId;
		const node = id
			? nodes.find((n): n is EventListenerRuleNode => n.id === id && n.type === "event_listener")
			: undefined;
		if (!node) return undefined;
		return {
			eventListenerName: node.data.name ?? "",
			targetScreenFieldIds: node.data.targetScreenFieldIds ?? [],
			targetScreenFieldNames: node.data.targetScreenFieldNames,
			action: node.data.action ?? "click",
		};
	}, [nodes, tracking.eventListener.activeNodeId]);

	const actionFormInitialState = useMemo<Partial<ActionFormState> | undefined>(() => {
		const id = tracking.action.activeNodeId;
		const node = id
			? nodes.find((n): n is ActionRuleNode => n.id === id && n.type === "action")
			: undefined;
		if (!node) return undefined;
		return {
			actionName: node.data.name ?? "",
			actionType: node.data.actionType ?? "click",
		};
	}, [nodes, tracking.action.activeNodeId]);

	const stopFormInitialState = useMemo<Partial<StopFormState> | undefined>(() => {
		const id = tracking.stop.activeNodeId;
		const node = id
			? nodes.find((n): n is StopRuleNode => n.id === id && n.type === "stop")
			: undefined;
		if (!node) return undefined;
		return {
			stopName: node.data.name ?? "",
			comment: node.data.comment ?? "",
			tags: normalizeStopTagNames(node.data.tags),
		};
	}, [nodes, tracking.stop.activeNodeId]);

	// --- Per-form save handlers (thin wrappers around updateNodeAndClose) ---

	const handleDecisionSave = useCallback((state: DecisionFormState) => {
		updateNodeAndClose("decision", "decision", state, (s) => ({
			name: s.decisionName,
			decisionLogicExpression: s.decisionLogicExpression,
			matchType: s.matchType,
			statements: s.statements.map((row) => {
				const {
					num,
					operator,
					databaseTableFieldId,
					screenFieldId,
					advancedTriggerId,
					resourceCollectionId,
					collectionName,
					databaseTableFieldName,
					screenFieldName,
					screenTableField,
					advancedTriggerName,
					simpleVariableName,
					formulaVariableName,
					resourceFormulaVariableId,
					resourceReactionNodeId,
					resourceReactionName,
				} = row;
				if (resourceReactionNodeId) {
					const baseReaction = {
						num,
						operator,
						resourceReactionNodeId,
						resourceReactionName,
						databaseTableFieldId: undefined,
						screenFieldId: undefined,
						advancedTriggerId: undefined,
						resourceCollectionId: undefined,
						collectionName: undefined,
						simpleVariableName: undefined,
						resourceFormulaVariableId: undefined,
						formulaVariableName: undefined,
						resourceFormulaVariable: undefined,
						databaseTableField: undefined,
						screenTableField: undefined,
						advancedTrigger: undefined,
						resourceCollection: undefined,
					};
					if (!isDecisionValueEntityRow(row)) {
						return {
							...baseReaction,
							plainTextValue: row.value,
							value: row.value,
						};
					}
					return {
						...baseReaction,
						plainTextValue: undefined,
						value: row.value,
						valueDatabaseTableFieldId: row.valueDatabaseTableFieldId,
						valueScreenFieldId: row.valueScreenFieldId,
						valueAdvancedTriggerId: row.valueAdvancedTriggerId,
						valueSimpleVariableId: row.valueSimpleVariableId,
						valueFormulaVariableId: row.valueFormulaVariableId,
						valueDatabaseSingleRowVariableId: row.valueDatabaseSingleRowVariableId,
						valueGlobalConstant: row.valueGlobalConstant,
						valueGlobalConstantName: row.valueGlobalConstantName,
						valueSimpleVariableName: row.valueSimpleVariableName,
						valueFormulaVariableName: row.valueFormulaVariableName,
						valueFormulaVariable: row.valueFormulaVariable,
						valueDatabaseSingleRowVariableName: row.valueDatabaseSingleRowVariableName,
						valueDatabaseTableField: row.valueDatabaseTableFieldName
							? { name: row.valueDatabaseTableFieldName }
							: undefined,
						valueScreenTableField: buildScreenFieldRef(
							row.valueScreenFieldName,
							undefined,
							row.valueScreenTableField ?? row.valueScreenField,
						),
						valueAdvancedTrigger: row.valueAdvancedTriggerName
							? { name: row.valueAdvancedTriggerName }
							: undefined,
						valueResourceCollectionId: row.valueResourceCollectionId,
						valueCollectionName: row.valueCollectionName,
						valueResourceCollection: row.valueCollectionName
							? { name: row.valueCollectionName }
							: undefined,
					};
				}
				if (resourceFormulaVariableId) {
					const baseFormula = {
						num,
						operator,
						resource: resourceFormulaVariableId,
						resourceFormulaVariableId,
						formulaVariableName,
						resourceFormulaVariable: row.resourceFormulaVariable,
						databaseTableFieldId: undefined,
						screenFieldId: undefined,
						advancedTriggerId: undefined,
						resourceCollectionId: undefined,
						collectionName: undefined,
						simpleVariableName: undefined,
						databaseTableField: undefined,
						screenTableField: undefined,
						advancedTrigger: undefined,
						resourceCollection: undefined,
						resourceReactionNodeId: undefined,
						resourceReactionName: undefined,
					};
					if (!isDecisionValueEntityRow(row)) {
						return {
							...baseFormula,
							plainTextValue: row.value,
							value: row.value,
						};
					}
					return {
						...baseFormula,
						plainTextValue: undefined,
						value: row.value,
						valueDatabaseTableFieldId: row.valueDatabaseTableFieldId,
						valueScreenFieldId: row.valueScreenFieldId,
						valueAdvancedTriggerId: row.valueAdvancedTriggerId,
						valueSimpleVariableId: row.valueSimpleVariableId,
						valueFormulaVariableId: row.valueFormulaVariableId,
						valueDatabaseSingleRowVariableId: row.valueDatabaseSingleRowVariableId,
						valueGlobalConstant: row.valueGlobalConstant,
						valueGlobalConstantName: row.valueGlobalConstantName,
						valueSimpleVariableName: row.valueSimpleVariableName,
						valueFormulaVariableName: row.valueFormulaVariableName,
						valueFormulaVariable: row.valueFormulaVariable,
						valueDatabaseSingleRowVariableName: row.valueDatabaseSingleRowVariableName,
						valueDatabaseTableField: row.valueDatabaseTableFieldName
							? { name: row.valueDatabaseTableFieldName }
							: undefined,
						valueScreenTableField: buildScreenFieldRef(
							row.valueScreenFieldName,
							undefined,
							row.valueScreenTableField ?? row.valueScreenField,
						),
						valueAdvancedTrigger: row.valueAdvancedTriggerName
							? { name: row.valueAdvancedTriggerName }
							: undefined,
						valueResourceCollectionId: row.valueResourceCollectionId,
						valueCollectionName: row.valueCollectionName,
						valueResourceCollection: row.valueCollectionName
							? { name: row.valueCollectionName }
							: undefined,
					};
				}
				const base = {
					num,
					operator,
					databaseTableFieldId,
					screenFieldId,
					advancedTriggerId,
					resourceCollectionId,
					collectionName,
					simpleVariableName,
					resourceFormulaVariableId: undefined,
					formulaVariableName: undefined,
					resourceFormulaVariable: undefined,
					databaseTableField: databaseTableFieldName ? { name: databaseTableFieldName } : undefined,
					screenTableField: buildScreenFieldRef(screenFieldName, undefined, screenTableField),
					advancedTrigger: advancedTriggerName ? { name: advancedTriggerName } : undefined,
					resourceCollection: collectionName ? { name: collectionName } : undefined,
				};
				if (!isDecisionValueEntityRow(row)) {
					return {
						...base,
						resourceReactionNodeId: undefined,
						resourceReactionName: undefined,
						plainTextValue: row.value,
						value: row.value,
					};
				}
				return {
					...base,
					resourceReactionNodeId: undefined,
					resourceReactionName: undefined,
					plainTextValue: undefined,
					value: row.value,
					valueDatabaseTableFieldId: row.valueDatabaseTableFieldId,
					valueScreenFieldId: row.valueScreenFieldId,
					valueAdvancedTriggerId: row.valueAdvancedTriggerId,
					valueSimpleVariableId: row.valueSimpleVariableId,
					valueFormulaVariableId: row.valueFormulaVariableId,
					valueDatabaseSingleRowVariableId: row.valueDatabaseSingleRowVariableId,
					valueGlobalConstant: row.valueGlobalConstant,
					valueGlobalConstantName: row.valueGlobalConstantName,
					valueSimpleVariableName: row.valueSimpleVariableName,
					valueFormulaVariableName: row.valueFormulaVariableName,
					valueFormulaVariable: row.valueFormulaVariable,
					valueDatabaseSingleRowVariableName: row.valueDatabaseSingleRowVariableName,
					valueDatabaseTableField: row.valueDatabaseTableFieldName
						? { name: row.valueDatabaseTableFieldName }
						: undefined,
					valueScreenTableField: buildScreenFieldRef(
						row.valueScreenFieldName,
						undefined,
						row.valueScreenTableField ?? row.valueScreenField,
					),
					valueAdvancedTrigger: row.valueAdvancedTriggerName
						? { name: row.valueAdvancedTriggerName }
						: undefined,
					valueResourceCollectionId: row.valueResourceCollectionId,
					valueCollectionName: row.valueCollectionName,
					valueResourceCollection: row.valueCollectionName
						? { name: row.valueCollectionName }
						: undefined,
				};
			}),
		}));
	}, [updateNodeAndClose]);

	const handleLoopSave = useCallback((state: LoopFormState) => {
		updateNodeAndClose("loop", "loop", state, (s) => {
			if (s.loopType === "variable") {
				return {
					name: s.loopName,
					label: s.loopName,
					loopType: "variable" as const,
					direction: undefined,
					databaseTableId: undefined,
					screenTableId: undefined,
					databaseTableVariableId: undefined,
					tableName: undefined,
					databaseTableName: undefined,
					screenTableName: undefined,
					databaseTableVariableName: undefined,
					collectionId: undefined,
					collectionName: undefined,
				};
			}
			const base = {
				name: s.loopName,
				label: s.loopName,
				loopType: "table" as const,
				direction: s.direction,
			};
			if (s.databaseTableVariableName) {
				return {
					...base,
					databaseTableVariableName: s.databaseTableVariableName,
					databaseTableId: undefined,
					screenTableId: undefined,
					databaseTableVariableId: undefined,
					tableName: undefined,
					databaseTableName: undefined,
					screenTableName: undefined,
					collectionId: undefined,
					collectionName: undefined,
				};
			}
			if (s.collectionId) {
				return {
					...base,
					collectionId: s.collectionId,
					collectionName: s.collectionName ?? s.tableName,
					databaseTableId: undefined,
					screenTableId: undefined,
					databaseTableVariableId: undefined,
					tableName: undefined,
					databaseTableName: undefined,
					screenTableName: undefined,
					databaseTableVariableName: undefined,
				};
			}
			return {
				...base,
				databaseTableId: s.databaseTableId,
				screenTableId: s.screenTableId,
				databaseTableVariableId: s.databaseTableVariableId,
				tableName: s.tableName,
				databaseTableName: s.databaseTableId ? s.tableName : undefined,
				screenTableName: s.screenTableId ? s.tableName : undefined,
				databaseTableVariableName: undefined,
				collectionId: undefined,
				collectionName: undefined,
			};
		});
	}, [updateNodeAndClose]);

	const handleSortSave = useCallback((state: SortFormState) => {
		updateNodeAndClose("sort", "sort", state, (s) => ({
			name: s.sortName,
			label: s.sortName,
			databaseTableId: s.databaseTableId,
			screenTableId: s.screenTableId,
			databaseTableVariableId: s.databaseTableVariableId,
			tableName: s.tableName,
			conditions: s.conditions.map(({ sortByDatabaseTableFieldId, sortByScreenTableFieldId, sortByFieldName, sortOrder }) => ({ sortByDatabaseTableFieldId, sortByScreenTableFieldId, sortByFieldName, sortOrder })),
		}));
	}, [updateNodeAndClose]);

	const handleFilterSave = useCallback((state: FilterFormState) => {
		updateNodeAndClose("filter", "filter", state, (s) => ({
			name: s.filterName,
			databaseTableId: s.databaseTableId,
			screenTableId: s.screenTableId,
			databaseTableVariableId: s.databaseTableVariableId,
			tableName: s.tableName,
			sortByDatabaseTableFieldId: s.sortByDatabaseTableFieldId,
			sortByScreenTableFieldId: s.sortByScreenTableFieldId,
			sortOrder: s.sortOrder,
			recordScope: s.recordScope,
			filterLogicExpression: s.filterLogicExpression,
			conditions: s.conditions.map((c) => {
				const trimmed = c.value.trim();
				const entity = isDecisionValueEntityRow(c);
				const base = {
					field: c.field,
					databaseTableFieldId: c.databaseTableFieldId,
					screenTableFieldId: c.screenTableFieldId,
					operator: c.operator,
				};
				if (!entity) {
					return {
						...base,
						value: trimmed,
						plainTextValue: trimmed,
					};
				}
				return {
					...base,
					value: trimmed,
					plainTextValue: undefined,
					valueDatabaseTableFieldId: c.valueDatabaseTableFieldId,
					valueScreenFieldId: c.valueScreenFieldId,
					valueDatabaseTableFieldName: c.valueDatabaseTableFieldName,
					valueScreenFieldName: c.valueScreenFieldName,
					valueScreenField: c.valueScreenField,
					valueScreenTableField: buildScreenFieldRef(
						c.valueScreenFieldName,
						undefined,
						c.valueScreenTableField ?? c.valueScreenField,
					),
					valueSimpleVariableName: c.valueSimpleVariableName,
					valueSimpleVariableId: c.valueSimpleVariableId,
					valueFormulaVariableId: c.valueFormulaVariableId,
					valueFormulaVariableName: c.valueFormulaVariableName,
					valueFormulaVariable: c.valueFormulaVariable,
					valueDatabaseSingleRowVariableId: c.valueDatabaseSingleRowVariableId,
					valueDatabaseSingleRowVariableName: c.valueDatabaseSingleRowVariableName,
					valueGlobalConstant: c.valueGlobalConstant,
					valueGlobalConstantName: c.valueGlobalConstantName,
				};
			}),
		}));
	}, [updateNodeAndClose]);

	const handleQuerySave = useCallback((state: QueryBuilderFormState) => {
		updateNodeAndClose("query_builder", "query_builder", state, (s) => ({
			name: s.queryName,
			table: s.databaseTableName,
			databaseTableName: s.databaseTableName,
			databaseTableId: s.databaseTableId,
			sortByFieldId: s.sortByFieldId,
			sortDirection: s.sortOrder === "Ascending" ? "asc" as const : "desc" as const,
			recordChoice: s.recordChoice,
			conditions: s.conditions.map(({ databaseTableFieldId, databaseTableFieldName, operator, value }) => ({ databaseTableFieldId, databaseTableFieldName, operator, value })),
		}));
	}, [updateNodeAndClose]);

	const handleReactionSave = useCallback((state: ReactionFormState) => {
		updateNodeAndClose("reaction", "reaction", state, (s) => {
			const base = {
				name: s.reactionName,
				reactionTypeId: s.reactionTypeId,
				reactionTypeName: s.reactionTypeName,
				reactionTypeFriendlyName: s.reactionTypeFriendlyName,
				screenFieldId: s.screenFieldId,
				screenFieldName: s.screenFieldName,
				reactionText: s.reactionText,
				minimumCharacterCount: undefined as number | undefined,
				userConfirmationText: undefined as string | undefined,
				showConfirmationText: undefined as boolean | undefined,
				caseSensitive: undefined as boolean | undefined,
			};
			if (isFreeResponseReactionType(s.reactionTypeName)) {
				const trimmed = s.minimumCharacterCount.trim();
				const parsed = /^\d+$/.test(trimmed) ? Number(trimmed) : undefined;
				if (parsed != null && parsed >= 1) {
					base.minimumCharacterCount = parsed;
				}
			}
			if (isUserConfirmationPopupReactionType(s.reactionTypeName)) {
				base.userConfirmationText = s.userConfirmationText;
				base.showConfirmationText = s.showConfirmationText;
				base.caseSensitive = s.caseSensitive;
			}
			return base;
		});
	}, [updateNodeAndClose]);

	/** Sync reaction node before Save (type picked / form opened with default type). */
	const patchActiveReactionNodeData = useCallback((patch: Partial<ReactionRuleNode["data"]>) => {
		const activeNodeId = trackingRef.current.reaction.activeNodeId;
		if (!activeNodeId) return;
		setNodes((currentNodes) =>
			currentNodes.map((node) =>
				node.id === activeNodeId && node.type === "reaction"
					? { ...node, data: { ...node.data, ...patch } }
					: node,
			),
		);
	}, [setNodes]);

	/** Live-sync stop node fields while the stop form stays open. */
	const patchActiveStopNodeData = useCallback((patch: Partial<StopRuleNode["data"]>) => {
		const activeNodeId = trackingRef.current.stop.activeNodeId;
		if (!activeNodeId) return;
		setNodes((currentNodes) =>
			currentNodes.map((node) =>
				node.id === activeNodeId && node.type === "stop"
					? { ...node, data: { ...node.data, ...patch } }
					: node,
			),
		);
	}, [setNodes]);

	const handleAssignmentSave = useCallback((state: AssignmentFormState) => {
		updateNodeAndClose("assignment", "assignment", state, (s) => ({
			name: s.assignmentName,
			statements: s.statements.map((row) => {
				const { variable, operator } = row;
				const base = { variable, operator };
				if (!isDecisionValueEntityRow(row)) {
					const text = row.value.trim();
					return {
						...base,
						plainTextValue: text,
						value: text,
					};
				}
				return {
					...base,
					plainTextValue: undefined,
					value: row.value,
					valueDatabaseTableFieldId: row.valueDatabaseTableFieldId,
					valueScreenFieldId: row.valueScreenFieldId,
					valueSimpleVariableId: row.valueSimpleVariableId,
					valueFormulaVariableId: row.valueFormulaVariableId,
					valueDatabaseSingleRowVariableId: row.valueDatabaseSingleRowVariableId,
					valueGlobalConstant: row.valueGlobalConstant,
					valueGlobalConstantName: row.valueGlobalConstantName,
					valueSimpleVariableName: row.valueSimpleVariableName,
					valueFormulaVariableName: row.valueFormulaVariableName,
					valueFormulaVariable: row.valueFormulaVariable,
					valueDatabaseSingleRowVariableName: row.valueDatabaseSingleRowVariableName,
					valueDatabaseTableField: row.valueDatabaseTableFieldName
						? { name: row.valueDatabaseTableFieldName }
						: undefined,
					valueScreenField: row.valueScreenField,
					valueScreenTableField: buildScreenFieldRef(
						row.valueScreenFieldName,
						undefined,
						row.valueScreenTableField ?? row.valueScreenField,
					),
				};
			}),
		}));
	}, [updateNodeAndClose]);

	const handleClearReactionSave = useCallback((state: ClearReactionFormState) => {
		updateNodeAndClose("clearReaction", "clear_reaction", state, (s) => ({
			name: s.clearReactionName,
			reactionNodeId: s.reactionNodeId,
			reactionNodeName: s.reactionNodeName,
		}));
	}, [updateNodeAndClose]);

	const handleEventListenerSave = useCallback((state: EventListenerFormState) => {
		updateNodeAndClose("eventListener", "event_listener", state, (s) => ({
			name: s.eventListenerName,
			targetScreenFieldIds: s.targetScreenFieldIds.filter(Boolean),
			targetScreenFieldNames: s.targetScreenFieldNames,
			action: s.action ?? "click",
		}));
	}, [updateNodeAndClose]);

	const handleActionSave = useCallback((state: ActionFormState) => {
		updateNodeAndClose("action", "action", state, (s) => ({
			name: s.actionName,
			actionType: s.actionType ?? "click",
		}));
	}, [updateNodeAndClose]);

	const handleStopSave = useCallback((state: StopFormState, shouldClose: boolean) => {
		updateNodeAndClose("stop", "stop", state, (s) => ({
			name: s.stopName,
			comment: s.comment,
			tags: s.tags,
		}));
		if (shouldClose) {
			closeNodeForm("stop", { fromSave: true });
		}
	}, [updateNodeAndClose]);

	return {
		formState,
		resetForms,
		openNodeForm,
		closeNodeForm,
		closeFormForNode,
		decisionFormInitialState,
		handleDecisionSave,
		loopFormInitialState,
		handleLoopSave,
		sortFormInitialState,
		handleSortSave,
		filterFormInitialState,
		handleFilterSave,
		queryFormInitialState,
		handleQuerySave,
		queryActiveNodeId: tracking.query_builder.activeNodeId,
		filterActiveNodeId: tracking.filter.activeNodeId,
		sortActiveNodeId: tracking.sort.activeNodeId,
		loopActiveNodeId: tracking.loop.activeNodeId,
		decisionActiveNodeId: tracking.decision.activeNodeId,
		reactionActiveNodeId: tracking.reaction.activeNodeId,
		assignmentActiveNodeId: tracking.assignment.activeNodeId,
		clearReactionActiveNodeId: tracking.clearReaction.activeNodeId,
		reactionFormInitialState,
		handleReactionSave,
		patchActiveReactionNodeData,
		assignmentFormInitialState,
		handleAssignmentSave,
		clearReactionFormInitialState,
		handleClearReactionSave,
		eventListenerFormInitialState,
		handleEventListenerSave,
		eventListenerActiveNodeId: tracking.eventListener.activeNodeId,
		actionFormInitialState,
		handleActionSave,
		actionActiveNodeId: tracking.action.activeNodeId,
		stopFormInitialState,
		handleStopSave,
		patchActiveStopNodeData,
		stopActiveNodeId: tracking.stop.activeNodeId,
	};
};

export type { FormModalKey, FormModalState, OpenFormOptions };
export { DEFAULT_FORM_STATE };
export default useFormModals;
